// FuturesScreenPage.js
import './FuturesScreenPage.css';
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import axios from 'axios';
import CoinCard from '../components/CoinCard';
import { Link } from 'react-router-dom';
import { SettingsContext } from '../SettingsContext';
import { useSelector } from 'react-redux';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Функция для глубокого сравнения массивов
const areArraysEqual = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;
  
  return arr1.every((item, index) => {
    const item1 = JSON.stringify(item);
    const item2 = JSON.stringify(arr2[index]);
    return item1 === item2;
  });
};

const FuturesScreenPage = ({ initData }) => {
  const [rawData, setRawData] = useState([]);
  const [list, setList] = useState([]);
  const audioRef = useRef(null);
  const { settings, setSettings } = useContext(SettingsContext);
  
  // Создаем список связок для звукового оповещения
  const soundSpreadList = useMemo(() => {
    return list.filter(card => +card.spread >= settings.minSpreadValueForSound);
  }, [list, settings.minSpreadValueForSound]);

  // Инициализация аудио
  useEffect(() => {
    const audio = new Audio('/sounds/notification.mp3');
    audio.preload = 'auto';
    audioRef.current = audio;

    return () => {
      audio.pause();
      audio.src = '';
    };
  }, []);

  // Воспроизведение звука при изменении soundSpreadList
  useEffect(() => {
    if (settings.soundEnabled && soundSpreadList.length > 0 && audioRef.current) {
      audioRef.current.play().catch(() => {});
    }
    const interval = setInterval(() => {
      if (settings.soundEnabled && soundSpreadList.length > 0 && audioRef.current) {
        audioRef.current.play().catch(() => {});
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [soundSpreadList, settings.soundEnabled]);

  const telegram_id = useMemo(() => {
    try {
      if (initData) {
        const params = new URLSearchParams(initData);
        const userStr = params.get('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          if (user?.id) return user.id;
        }
      }
      // Fallback: из localStorage
      const fallbackId = localStorage.getItem('fallback_telegram_id');
      return fallbackId ? Number(fallbackId) : null;
    } catch (error) {
      console.error('Ошибка при парсинге initData:', error);
      return null;
    }
  }, [initData]);

  const subscriptionData = useSelector(state => state.subscriptionData);

  const fetchData = async () => {
    if (!telegram_id) {
      console.warn('Отсутствует telegram_id');
      return;
    }

    if (!subscriptionData?.subscription_active) {
      console.log('Подписка неактивна, запрос данных пропущен');
      return;
    }

    try {
      const headers = {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      };
      let url = `${API_BASE_URL}/arbitragecoinsdata`;
      if (initData) {
        url += `?${new URLSearchParams({ initData }).toString()}`;
      } else {
        const fallbackToken = localStorage.getItem('fallback_access_token');
        if (fallbackToken) headers['Authorization'] = `Bearer ${fallbackToken}`;
      }

      const response = await axios.get(url, { headers });

      const jsonData = await response.data || [];
      setRawData(jsonData);
    } catch (error) {
      console.error('Error fetching data:', error.message);
      console.error('Error config:', error.config);
      setRawData([]);
    }
  };

  const toggleSound = () => setSettings((prevSettings) => ({ ...prevSettings, soundEnabled: !settings.soundEnabled }));

  useEffect(() => {
    if (telegram_id && subscriptionData?.subscription_active) {
      fetchData();
      const intervalId = setInterval(fetchData, 1000);
      return () => clearInterval(intervalId);
    }
  }, [telegram_id, subscriptionData?.subscription_active]);

  

  useEffect(() => {
    // Проверка на существование settings и rawData
    if (!settings || !rawData || !rawData.length) {
      setList([]);
      return;
    }

    let filteredData = rawData;

    // Применяем белый список, если он задан
    if (settings.tokenWhitelist && settings.tokenWhitelist.trim() !== '') {
      const whitelist = settings.tokenWhitelist
        .split(',')
        .map((token) => token.trim().toLowerCase())
        .filter((token) => token !== '');
      filteredData = filteredData.filter((item) => {
        const tokenName = item.tokenName?.toLowerCase();
        return tokenName && whitelist.includes(tokenName);
      });
    } else {
        // Фильтрация по биржам
        filteredData = filteredData.filter((item) => {
            if (item.spotExhangeName || item.futuresExhangeName) {
                // Спот + фьючерс
                const spotExchange = item.spotExhangeName?.toLowerCase();
                const futuresExchange = item.futuresExhangeName?.toLowerCase();

                const spotExchangeIncluded = spotExchange && settings.exchanges[spotExchange];
                const futuresExchangeIncluded = futuresExchange && settings.exchanges[futuresExchange];
                return spotExchangeIncluded && futuresExchangeIncluded;
            } else if (item.futuresExchange1 || item.futuresExchange2) {
                // Фьючерс + фьючерс
                const futuresExchange1 = item.futuresExchange1?.toLowerCase();
                const futuresExchange2 = item.futuresExchange2?.toLowerCase();

                const futures1Included = futuresExchange1 && settings.exchanges[futuresExchange1];
                const futures2Included = futuresExchange2 && settings.exchanges[futuresExchange2];
                return futures1Included && futures2Included;
            }
            return false;
        });

        // Фильтрация по черному списку монет
        const tokenBlacklist = settings.tokenBlacklist
            .split(',')
            .map((token) => token.trim().toLowerCase())
            .filter((token) => token !== '');
        if (tokenBlacklist.length > 0) {
            filteredData = filteredData.filter((item) => {
                const tokenName = item.tokenName?.toLowerCase();
                return tokenName && !tokenBlacklist.includes(tokenName);
            });
        }

        // Фильтрация по profit
        filteredData = filteredData.filter((item) => {
            const profit = parseFloat(item.profit);
            return !isNaN(profit) && profit >= settings.minProfit && profit <= settings.maxProfit;
        });

        // Фильтрация по объему
        filteredData = filteredData.filter((item) => {
          if (item.volumeInDollars) {
              // Учитываем объем только для стратегий, где он есть
              const volume = parseFloat(item.volumeInDollars);
              return !isNaN(volume) && volume >= settings.minVolume;
          }
          // Если объема нет (например, для "фьючерс + фьючерс"), пропускаем фильтрацию
          return true;
        });

        // Фильтрация по черным спискам бирж
        filteredData = filteredData.filter((item) => {
            const tokenName = item.tokenName?.toLowerCase();
            if (!tokenName) return false;

            if (item.spotExhangeName || item.futuresExhangeName) {
                // Спот + фьючерс
                const spotExchange = item.spotExhangeName?.toLowerCase();
                const futuresExchange = item.futuresExhangeName?.toLowerCase();

                const spotBlacklist = spotExchange
                    ? settings.exchangeBlacklists[spotExchange]?.buy
                          ?.split(',')
                          ?.map((token) => token.trim().toLowerCase())
                          ?.filter((token) => token !== '')
                    : [];
                const futuresBlacklist = futuresExchange
                    ? settings.exchangeBlacklists[futuresExchange]?.sell
                          ?.split(',')
                          ?.map((token) => token.trim().toLowerCase())
                          ?.filter((token) => token !== '')
                    : [];

                if (spotBlacklist && spotBlacklist.includes(tokenName)) return false;
                if (futuresBlacklist && futuresBlacklist.includes(tokenName)) return false;
            } else if (item.futuresExchange1 || item.futuresExchange2) {
                // Фьючерс + фьючерс
                const futuresExchange1 = item.futuresExchange1?.toLowerCase();
                const futuresExchange2 = item.futuresExchange2?.toLowerCase();

                const futures1Blacklist = futuresExchange1
                    ? settings.exchangeBlacklists[futuresExchange1]?.buy
                          ?.split(',')
                          ?.map((token) => token.trim().toLowerCase())
                          ?.filter((token) => token !== '')
                    : [];
                const futures2Blacklist = futuresExchange2
                    ? settings.exchangeBlacklists[futuresExchange2]?.sell
                          ?.split(',')
                          ?.map((token) => token.trim().toLowerCase())
                          ?.filter((token) => token !== '')
                    : [];

                if (futures1Blacklist && futures1Blacklist.includes(tokenName)) return false;
                if (futures2Blacklist && futures2Blacklist.includes(tokenName)) return false;
            }

            return true;
        });
    }

    
    const newList = filteredData.slice(0, 100);
    
    // Проверяем, действительно ли данные изменились
    if (!areArraysEqual(list, newList)) {
      setList(newList);
    }
}, [rawData, settings]);

  return (
    <div className='futures-wrapper'>
      <header className={`futures-header`}>
        {/* <div class="blur-container"></div>
        <div className='futures-title'>
          <svg xmlns="http://www.w3.org/2000/svg" version="1.0" width="1024px" height="1024px" viewBox="0 0 1024 1024" preserveAspectRatio="xMidYMid meet">
          <g fill="#202020">
            <path d="M705.40 725.25 c-6 -1.15 -9.90 -2.95 -14 -6.45 -2 -1.75 -9.40 -9 -16.45 -16.10 -7.05 -7.15 -16.70 -16.65 -21.45 -21.20 -9.50 -9 -19.65 -20.15 -21.80 -23.75 l-1.35 -2.35 5.70 -5.30 c3.15 -2.90 12.90 -12.45 21.70 -21.15 8.80 -8.75 17.70 -17.25 19.75 -18.85 l3.75 -3 2.75 1.40 c3.45 1.80 5.45 3.45 17.50 14.70 13.05 12.15 17.95 16.90 33.15 31.95 15.05 14.95 15.35 15.45 16.65 25.05 1.20 8.75 0.35 15.25 -2.90 22.20 -5.20 11.05 -13 18.40 -23.30 21.95 -3.20 1.10 -4.85 1.35 -9.85 1.50 -3.70 0.05 -7.50 -0.15 -9.85 -0.60z"/>
            <path d="M495.50 664 c-13.90 -0.80 -30.75 -3.95 -47 -8.85 -8.25 -2.50 -25.40 -9.10 -31.55 -12.15 -27 -13.50 -48.10 -29.30 -65.90 -49.25 -7.50 -8.40 -10.20 -11.90 -15.90 -20.50 -9.95 -15.05 -16.75 -27.25 -22.05 -39.75 -2.50 -5.95 -7.05 -20.10 -9.30 -29.10 -2.30 -9.05 -4.65 -24.70 -5.30 -34.65 -0.65 -10.45 -0.65 -30.80 0.05 -39.30 0.75 -9.75 4 -28.35 6.70 -38.45 2.25 -8.40 8.30 -24.65 11.90 -31.75 2.55 -5.15 9.85 -17.75 13.60 -23.50 11.15 -17.15 28.90 -36.20 44.25 -47.55 12.45 -9.25 16.20 -11.75 25.45 -17.30 11.40 -6.80 19.30 -10.75 30.35 -15.05 23.95 -9.40 40.70 -13.35 63.45 -14.95 20.95 -1.55 41.95 -0.10 61.75 4.15 14.15 3 34.30 10.20 48.75 17.30 8.20 4.05 24.70 14.30 31.15 19.35 15.55 12.20 22.25 18.85 34.80 34.55 9.65 12 12.15 15.45 17.15 23.50 6.45 10.40 9.05 15.30 13.35 24.85 10.15 22.75 15.70 42.90 17.90 65.15 0.80 8.25 0.80 33.15 0 40.75 -1.25 11.45 -5.55 32.95 -8.60 42.85 -1.50 4.85 -7.30 19.75 -10.10 25.85 -3.95 8.65 -9.55 18.50 -16.50 28.95 -5.15 7.80 -9.65 13.45 -19.70 24.60 -33.25 37.15 -63.65 55.30 -109.55 65.55 -20.95 4.65 -36.80 5.90 -59.15 4.70z m32.35 -42.75 c11.80 -1.45 17.35 -2.80 34.45 -8.25 9.25 -2.95 21.50 -8.05 27.20 -11.25 7.25 -4.10 18.50 -12.15 28.50 -20.45 8.85 -7.30 22.20 -22.50 28.60 -32.55 8.75 -13.75 15.75 -28.25 20.35 -42.25 4.60 -14.15 6 -20.20 8.40 -36.50 1.20 -8.20 1.20 -26.95 -0.05 -37.75 -2.65 -24.10 -9.90 -45.10 -23.05 -67 -6.80 -11.30 -9.35 -14.70 -18.65 -25.20 -14.05 -15.80 -32 -29.50 -51.10 -39 -19.70 -9.80 -43.90 -15.65 -69.75 -16.85 -22.25 -1.05 -52.85 5.35 -74.30 15.50 -5.60 2.60 -17.90 9.30 -22.20 12.05 -13.20 8.45 -30.25 24.15 -41.45 38.20 -11.60 14.50 -24.10 40.35 -29.20 60.30 -1.95 7.55 -2.35 9.95 -3.60 20.85 -3.15 27.95 1.15 60.35 11.55 86.30 2.95 7.40 10 21.60 13.50 27.20 11.60 18.50 34.90 41.20 52.70 51.40 3.05 1.70 6.20 3.65 7.05 4.30 1.55 1.15 9.30 5.20 9.95 5.20 0.20 0 2.95 1.10 6.15 2.45 19 8 34.40 11.80 55.85 13.70 5.90 0.55 23.55 0.30 29.10 -0.40z"/>
            <path d="M525.95 592.80 c-0.10 -0.10 -7.70 -0.30 -16.80 -0.40 l-16.65 -0.15 -0.10 -10.25 c-0.10 -5.65 -0.10 -13.80 0 -18.10 l0.20 -7.85 -1.20 -0.25 c-13.50 -2.50 -21.80 -5.70 -33.90 -13.05 -8.55 -5.15 -18.80 -13.95 -23.95 -20.45 -3.25 -4.15 -10.50 -16.20 -14.30 -23.80 -1.75 -3.45 -3.85 -8.60 -4.70 -11.50 -0.85 -2.90 -1.75 -5.75 -1.95 -6.40 l-0.35 -1.10 -16.75 -0.20 c-9.20 -0.15 -17.10 -0.35 -17.55 -0.55 -0.70 -0.25 -0.90 -1.65 -1.20 -9.15 -0.40 -8.70 0 -32.35 0.55 -33.20 0.15 -0.25 1 -0.30 1.85 -0.15 0.90 0.15 7.35 0.50 14.35 0.80 7 0.30 14.25 0.65 16.05 0.80 l3.30 0.25 0.30 -4.20 c0.90 -12.05 7.85 -27 18.75 -40.50 4.55 -5.60 16.40 -16.85 20.85 -19.80 4.90 -3.20 19.20 -10.55 24 -12.25 3.90 -1.40 12.45 -3.35 14.65 -3.35 l1.35 0 -0.15 -18.25 -0.20 -18.25 2.40 0.40 c1.35 0.20 9.90 0.35 19 0.35 l16.55 0 0.35 7.55 c0.15 4.15 0.15 12.20 -0.05 17.90 l-0.30 10.30 2.50 0 c7.35 0 26.15 8.70 40.85 18.90 11 7.60 19.85 16.60 27 27.35 l3.30 5 -1.50 0.40 c-1.90 0.50 -8.95 5.45 -14.25 10 -6.35 5.50 -9.25 7.60 -13.25 9.60 -2.05 1 -4.35 2.40 -5.05 3.05 -0.70 0.70 -1.60 1.10 -2 0.95 -0.40 -0.15 -1.80 -1.90 -3.15 -3.90 -8.50 -12.40 -12.60 -16.75 -21 -21.80 -6.55 -3.95 -12.10 -6.20 -19.45 -7.90 -7.80 -1.80 -18.40 -1.90 -25.30 -0.20 -5.55 1.40 -11.45 3.50 -15.55 5.55 -2.95 1.50 -9.95 6.80 -12.75 9.70 -6.40 6.65 -12.85 17.85 -14.95 26.05 -3.95 15.80 -1.05 35.05 7.25 47.35 8.65 12.85 19.25 21.45 30.70 24.90 5.85 1.75 9.90 2.25 18.75 2.25 9.15 0 9.65 -0.05 18.50 -2.85 13.75 -4.30 25.15 -14.35 32.30 -28.50 1.20 -2.45 2.50 -4.40 2.85 -4.40 0.80 0 4.60 2.40 9.70 6.05 2.15 1.55 5.95 4 8.40 5.45 6.10 3.60 16.65 10.60 18.95 12.60 l1.85 1.65 -5.50 7 c-9.80 12.50 -16.50 19.25 -26.45 26.60 -11.30 8.40 -28.55 15.80 -39.70 17.05 l-3.05 0.35 0.10 9 c0 4.95 0.20 13.20 0.35 18.40 l0.35 9.35 -2.50 0 c-1.35 0 -2.60 -0.10 -2.70 -0.20z"/>
            <path d="M311.95 662.80 c-5.65 -2.30 -9.20 -5.40 -11.45 -10.05 -1.05 -2.10 -1.25 -3.25 -1.25 -7.05 0 -4.30 0.10 -4.70 2.10 -8.80 1.65 -3.30 2.90 -5 5.40 -7.35 4.75 -4.40 6.50 -5.20 12.35 -5.45 4.60 -0.20 5.05 -0.15 8.15 1.35 4.20 1.95 8.10 5.90 10.35 10.40 1.50 3.05 1.65 3.80 1.65 7.90 0 4.10 -0.15 4.80 -1.65 7.80 -1.85 3.65 -4.85 7.55 -7.25 9.30 -4.50 3.30 -12.95 4.25 -18.40 1.95z"/>
            <path d="M314.30 290.95 c-3.65 -0.85 -6.75 -3 -9.35 -6.40 -3.60 -4.80 -4.65 -7.80 -4.70 -13.30 0 -5.50 1 -7.95 4.85 -12.05 3 -3.15 4.90 -4.30 9.25 -5.40 6.80 -1.75 13.40 -0.60 18.50 3.25 5.15 3.85 7.35 8.40 7.40 15.20 0 3.30 -0.25 4.50 -1.30 6.75 -1.55 3.35 -5.80 8.10 -8.80 9.85 -4.05 2.35 -10.85 3.25 -15.85 2.10z"/>
            <path d="M691.25 290.10 c-4.75 -2.30 -9.85 -7.95 -11.80 -13.10 -1.50 -3.95 -1.55 -9.50 -0.10 -12.65 1.45 -3.10 6.75 -8.60 9.80 -10.15 6.05 -3.10 12.55 -3.45 17.70 -0.90 7.25 3.55 11.15 9.85 11.15 18.20 0 5.05 -1.80 8.95 -6.20 13.25 -4.10 4 -8.20 5.80 -14.30 6.10 -3.45 0.15 -4.60 0 -6.25 -0.75z"/>
          </g>
          </svg>
          <span>Фьючерсный скринер</span>
        </div> */}
        <div className='futures-settings'>
          <button className={`futures-settings-sound_button ${!settings.soundEnabled ? 'muted' : ''}`} onClick={toggleSound}>
            <img src={"/images/sound.png"} className={`futures-settings-sound_img`} alt="sound" />
          </button>
          <Link className="futures-settings_button" to="/settings">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <g clipPath="url(#clip0_7_481)">
                <path d="M10 12.5C11.3807 12.5 12.5 11.3807 12.5 10C12.5 8.61929 11.3807 7.5 10 7.5C8.61929 7.5 7.5 8.61929 7.5 10C7.5 11.3807 8.61929 12.5 10 12.5Z" stroke="black" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M16.1668 12.5C16.0559 12.7513 16.0228 13.0302 16.0718 13.3005C16.1208 13.5708 16.2497 13.8203 16.4418 14.0167L16.4918 14.0667C16.6468 14.2215 16.7697 14.4053 16.8536 14.6076C16.9375 14.8099 16.9806 15.0268 16.9806 15.2458C16.9806 15.4649 16.9375 15.6817 16.8536 15.8841C16.7697 16.0864 16.6468 16.2702 16.4918 16.425C16.337 16.58 16.1532 16.7029 15.9509 16.7868C15.7486 16.8706 15.5317 16.9138 15.3127 16.9138C15.0936 16.9138 14.8768 16.8706 14.6744 16.7868C14.4721 16.7029 14.2883 16.58 14.1335 16.425L14.0835 16.375C13.8871 16.1829 13.6376 16.054 13.3673 16.005C13.097 15.956 12.8182 15.9891 12.5668 16.1C12.3204 16.2056 12.1101 16.381 11.9621 16.6046C11.814 16.8282 11.7346 17.0902 11.7335 17.3583V17.5C11.7335 17.942 11.5579 18.366 11.2453 18.6785C10.9328 18.9911 10.5089 19.1667 10.0668 19.1667C9.6248 19.1667 9.20088 18.9911 8.88832 18.6785C8.57576 18.366 8.40016 17.942 8.40016 17.5V17.425C8.39371 17.1492 8.30443 16.8817 8.14392 16.6573C7.98342 16.4328 7.75911 16.2619 7.50016 16.1667C7.24882 16.0557 6.97 16.0226 6.69967 16.0717C6.42934 16.1207 6.1799 16.2496 5.9835 16.4417L5.9335 16.4917C5.77871 16.6466 5.59489 16.7696 5.39256 16.8534C5.19023 16.9373 4.97336 16.9805 4.75433 16.9805C4.5353 16.9805 4.31843 16.9373 4.1161 16.8534C3.91377 16.7696 3.72995 16.6466 3.57516 16.4917C3.4202 16.3369 3.29727 16.1531 3.2134 15.9507C3.12952 15.7484 3.08635 15.5315 3.08635 15.3125C3.08635 15.0935 3.12952 14.8766 3.2134 14.6743C3.29727 14.4719 3.4202 14.2881 3.57516 14.1333L3.62516 14.0833C3.81728 13.8869 3.94615 13.6375 3.99517 13.3672C4.04418 13.0968 4.01109 12.818 3.90016 12.5667C3.79453 12.3202 3.61913 12.11 3.39555 11.9619C3.17198 11.8139 2.90999 11.7344 2.64183 11.7333H2.50016C2.05814 11.7333 1.63421 11.5577 1.32165 11.2452C1.00909 10.9326 0.833496 10.5087 0.833496 10.0667C0.833496 9.62464 1.00909 9.20072 1.32165 8.88816C1.63421 8.5756 2.05814 8.4 2.50016 8.4H2.57516C2.85099 8.39355 3.1185 8.30427 3.34291 8.14376C3.56732 7.98325 3.73826 7.75895 3.8335 7.5C3.94443 7.24865 3.97752 6.96984 3.9285 6.69951C3.87948 6.42918 3.75061 6.17973 3.5585 5.98333L3.5085 5.93333C3.35354 5.77855 3.2306 5.59473 3.14673 5.3924C3.06286 5.19007 3.01969 4.97319 3.01969 4.75417C3.01969 4.53514 3.06286 4.31826 3.14673 4.11593C3.2306 3.9136 3.35354 3.72979 3.5085 3.575C3.66328 3.42004 3.8471 3.29711 4.04943 3.21323C4.25176 3.12936 4.46864 3.08619 4.68766 3.08619C4.90669 3.08619 5.12357 3.12936 5.3259 3.21323C5.52823 3.29711 5.71204 3.42004 5.86683 3.575L5.91683 3.625C6.11323 3.81711 6.36268 3.94599 6.63301 3.995C6.90333 4.04402 7.18215 4.01093 7.4335 3.9H7.50016C7.74664 3.79436 7.95684 3.61896 8.10491 3.39539C8.25297 3.17181 8.33243 2.90982 8.3335 2.64167V2.5C8.3335 2.05797 8.50909 1.63405 8.82165 1.32149C9.13421 1.00893 9.55814 0.833334 10.0002 0.833334C10.4422 0.833334 10.8661 1.00893 11.1787 1.32149C11.4912 1.63405 11.6668 2.05797 11.6668 2.5V2.575C11.6679 2.84316 11.7474 3.10515 11.8954 3.32872C12.0435 3.5523 12.2537 3.7277 12.5002 3.83333C12.7515 3.94426 13.0303 3.97735 13.3007 3.92834C13.571 3.87932 13.8204 3.75045 14.0168 3.55833L14.0668 3.50833C14.2216 3.35337 14.4054 3.23044 14.6078 3.14657C14.8101 3.06269 15.027 3.01952 15.246 3.01952C15.465 3.01952 15.6819 3.06269 15.8842 3.14657C16.0866 3.23044 16.2704 3.35337 16.4252 3.50833C16.5801 3.66312 16.7031 3.84694 16.7869 4.04927C16.8708 4.2516 16.914 4.46847 16.914 4.6875C16.914 4.90653 16.8708 5.1234 16.7869 5.32573C16.7031 5.52806 16.5801 5.71188 16.4252 5.86667L16.3752 5.91667C16.183 6.11307 16.0542 6.36251 16.0052 6.63284C15.9561 6.90317 15.9892 7.18199 16.1002 7.43333V7.5C16.2058 7.74648 16.3812 7.95668 16.6048 8.10474C16.8284 8.25281 17.0903 8.33226 17.3585 8.33333H17.5002C17.9422 8.33333 18.3661 8.50893 18.6787 8.82149C18.9912 9.13405 19.1668 9.55797 19.1668 10C19.1668 10.442 18.9912 10.866 18.6787 11.1785C18.3661 11.4911 17.9422 11.6667 17.5002 11.6667H17.4252C17.157 11.6677 16.895 11.7472 16.6714 11.8953C16.4479 12.0433 16.2725 12.2535 16.1668 12.5Z" stroke="black" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
              </g>
              <defs>
                <clipPath id="clip0_7_481">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>
          </Link>
        </div>
      </header>
      <div className="coin-list">
        {subscriptionData?.subscription_active ? (
          Array.isArray(list) && list.length > 0 ? (
            list.map((coin, index) => <CoinCard key={index} coin={coin} />)
          ) : (
            <div className="no-coins">
              <p className="no-coins-text">Связок не нашлось :(</p>
              <p className="no-coins-text">Подожди чуть-чуть</p>
              <p className="no-coins-text">Или попробуй изменить настройки</p>
            </div>
          )
        ) : (
          <div className="no-coins">
            <p className="no-coins-text">Для просмотра связок</p>
            <p className="no-coins-text">необходимо продлить подписку</p>
            <div className="profile-pay-button help-and-payment">
              <a href='https://metod-payscanner.ru/#oplata' target='_blank' rel='noreferrer' className="link-btn link-guide">Продлить</a>
              {/* <PayButton initData={initData}/> */}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FuturesScreenPage;
